import { createSelector } from "@reduxjs/toolkit";
import { buildDashboardData, parseGraphDataForRecharts, computeNonEsVacuumLevelDomain, } from "./graph-data-transforms";
import { ATM_TOLERANCE, USE_LOCAL_GRAPH_DATA, } from "ui/components/panels/graphs/constants";
import { findCupReleaseIndex, findCupsSettledTimes, getGoverningPump, getSuctionCups, getVacuumPumps, } from "ui/components/panels/graphs/common/helper-functions";
import { mockPerformanceGraphData } from "ui/components/panels/graphs/TEST-DATA/local-test-data";
import { compareFindingSeverity, groupFlowRestrictionFindings, } from "utility/flow-restrictions";
export var selectPerformanceRoot = function (state) {
    return state.systemPerformance;
};
// The displayed layer follows ui.activeLayer; result-bearing fields are read
// from that layer so the entire downstream selector chain is layer-agnostic.
export var selectActiveLayerPerformance = function (state) {
    return selectPerformanceRoot(state).byLayer[state.ui.activeLayer];
};
// Per-layer results record — the calculation observer iterates this to drive a
// without-ES → with-ES request chain per layer.
export var selectPerformanceByLayer = function (state) {
    return selectPerformanceRoot(state).byLayer;
};
export var selectPerformanceData = function (state) {
    return selectActiveLayerPerformance(state).data;
};
export var selectPreloadedPerformance = function (state) {
    return selectActiveLayerPerformance(state).preloadedData;
};
export var selectPerformanceSystemDataSnapshot = function (state) {
    return selectPerformanceRoot(state).systemDataSnapshot;
};
export var selectIsPerformanceLoading = function (state) {
    return selectPerformanceRoot(state).isLoading;
};
export var selectPerformanceError = function (state) {
    return selectActiveLayerPerformance(state).error;
};
export var selectIsPerformanceDataValid = function (state) {
    return selectActiveLayerPerformance(state).isPerformanceDataValid;
};
export var selectHasSettledUserCalculation = function (state) {
    return selectPerformanceRoot(state).hasSettledUserCalculation;
};
export var selectShowEnergySave = function (state) {
    return selectPerformanceRoot(state).showEnergySavePerformance;
};
export var selectPlayLocationPercent = function (state) {
    return selectPerformanceRoot(state).playLocationPercent;
};
export var selectSanityCheckData = function (state) {
    return selectActiveLayerPerformance(state).sanityCheckData;
};
export var selectIsPerformanceDataPending = function (state) {
    return selectActiveLayerPerformance(state).isPerformanceDataPending;
};
/*
 * Cups the shaped-object raycast has flagged as off the object. Raw slice
 * state — everything else should read the application-aware
 * `selectCupsOutsideOfObject`(`ByLayer`) from store/selectors.ts, which merges
 * this with the geometric cup-to-box assignment used by cardboard.
 */
export var selectRaycastCupsOutsideOfObject = function (state) {
    return selectPerformanceRoot(state).cupsOutsideOfObject;
};
export var selectCalculationsEnabled = function (state) {
    return selectPerformanceRoot(state).calculationsEnabled;
};
export var selectIsEnergySaveAvailableForSystem = createSelector(selectPerformanceData, function (performanceData) {
    return (performanceData === null || performanceData === void 0 ? void 0 : performanceData.energySavingOn) !== undefined;
});
export var selectHasReachedEnergySave = createSelector(selectPerformanceData, function (performanceData) {
    return ((performanceData === null || performanceData === void 0 ? void 0 : performanceData.energySavingOn) &&
        performanceData.energySavingOn.systemTimeToEnergySave > 0);
});
export var selectActivePerformanceData = createSelector(selectPerformanceData, selectShowEnergySave, function (performanceData, showEnergySave) {
    if (!performanceData)
        return;
    if (showEnergySave && performanceData.energySavingOn) {
        return performanceData.energySavingOn;
    }
    else if (!showEnergySave) {
        return performanceData.energySavingOff;
    }
    else {
        return undefined;
    }
});
export var selectIsPostCalculation = createSelector(selectIsPerformanceDataValid, function (isPerformanceDataValid) {
    if (isPerformanceDataValid) {
        return true;
    }
    else {
        return false;
    }
});
export var selectHasReachedPartSecured = createSelector(selectActivePerformanceData, function (activePerformanceData) {
    return activePerformanceData
        ? activePerformanceData.systemTimeToPartSecured > 0
        : false;
});
export var selectVacuumPumpPerformance = function (nodeId) {
    return createSelector(selectActivePerformanceData, function (activePerformanceData) { var _a; return (_a = activePerformanceData === null || activePerformanceData === void 0 ? void 0 : activePerformanceData.nodeData[nodeId]) === null || _a === void 0 ? void 0 : _a.vacuumPump; });
};
export var selectSuctionCupPerformance = function (nodeId) {
    return createSelector(selectActivePerformanceData, function (activePerformanceData) { var _a; return (_a = activePerformanceData === null || activePerformanceData === void 0 ? void 0 : activePerformanceData.nodeData[nodeId]) === null || _a === void 0 ? void 0 : _a.suctionCup; });
};
/* The shown result, or one that settled but is withheld because it failed
 * (e.g. part secured not reached). The sense valves/restrictors are read from
 * it, since a sense valve that stays open outside of the part is a common
 * reason for such a failure, and so are a few pump values that help tell why
 * it failed. */
export var selectShownOrWithheldPerformanceData = createSelector(selectIsPerformanceDataValid, selectActivePerformanceData, selectHasSettledUserCalculation, selectIsPerformanceLoading, selectPreloadedPerformance, selectShowEnergySave, function (isPerformanceDataValid, activePerformanceData, hasSettledUserCalculation, isLoading, withheldPerformance, showEnergySave) {
    if (isPerformanceDataValid)
        return activePerformanceData;
    if (!hasSettledUserCalculation || isLoading || !withheldPerformance)
        return;
    return ((showEnergySave && withheldPerformance.energySavingOn) ||
        withheldPerformance.energySavingOff);
});
export var selectShownOrWithheldVacuumPumpPerformance = function (nodeId) {
    return createSelector(selectShownOrWithheldPerformanceData, function (performanceData) { var _a; return (_a = performanceData === null || performanceData === void 0 ? void 0 : performanceData.nodeData[nodeId]) === null || _a === void 0 ? void 0 : _a.vacuumPump; });
};
export var selectFlowRestrictionPerformance = function (nodeId) {
    return createSelector(selectShownOrWithheldPerformanceData, function (performanceData) { var _a; return (_a = performanceData === null || performanceData === void 0 ? void 0 : performanceData.nodeData[nodeId]) === null || _a === void 0 ? void 0 : _a.flowRestriction; });
};
/* The calculation API's feedback on the sense valves/restrictors of the same
 * result; undefined when it sent none */
var selectFlowRestrictionFindings = createSelector(selectShownOrWithheldPerformanceData, function (performanceData) { return performanceData === null || performanceData === void 0 ? void 0 : performanceData.flowRestrictionFindings; });
export var selectFlowRestrictionFindingGroups = createSelector(selectFlowRestrictionFindings, function (findings) { return findings && groupFlowRestrictionFindings(findings); });
/* The worst finding on each grip position's sense valve/restrictor. A
 * decentral pump's findings share its grip position's node but are not about
 * the valve. */
export var selectFlowRestrictionFindingSeverities = createSelector(selectFlowRestrictionFindings, function (findings) {
    var severities = {};
    (findings !== null && findings !== void 0 ? findings : []).forEach(function (_a) {
        var nodeId = _a.nodeId, isPump = _a.isPump, severity = _a.severity;
        if (isPump)
            return;
        var current = severities[nodeId];
        if (!current || compareFindingSeverity(severity, current) < 0) {
            severities[nodeId] = severity;
        }
    });
    return severities;
});
export var selectDashboardData = createSelector(selectActivePerformanceData, selectPerformanceSystemDataSnapshot, selectHasReachedPartSecured, function (activePerformanceData, systemDataSnapshot, hasReachedPartSecured) {
    if (!activePerformanceData || !systemDataSnapshot)
        return;
    if (!hasReachedPartSecured)
        return;
    return buildDashboardData(activePerformanceData, systemDataSnapshot);
});
export var selectGraphData = createSelector(selectActivePerformanceData, selectSanityCheckData, selectShowEnergySave, function (performanceData, sanityCheckData, showEnergySave) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    var nodeData = (_a = performanceData === null || performanceData === void 0 ? void 0 : performanceData.nodeData) !== null && _a !== void 0 ? _a : {};
    var psIndex = (_b = performanceData === null || performanceData === void 0 ? void 0 : performanceData.psIndex) !== null && _b !== void 0 ? _b : 0;
    var esIndex = (_c = performanceData === null || performanceData === void 0 ? void 0 : performanceData.esIndex) !== null && _c !== void 0 ? _c : 0;
    var placeStopIndex = (_d = performanceData === null || performanceData === void 0 ? void 0 : performanceData.placeStopIndex) !== null && _d !== void 0 ? _d : 0;
    var placeStartIndex = (_e = performanceData === null || performanceData === void 0 ? void 0 : performanceData.placeStartIndex) !== null && _e !== void 0 ? _e : 0;
    // placeStopIndex (the API place_stop) marks place_start + blow_off_time_to_atm,
    // which lands while the cups still hold vacuum; derive the real release
    // point from the cup curves instead, falling back to placeStopIndex.
    var cupReleaseIndex = findCupReleaseIndex(getSuctionCups(nodeData), placeStartIndex, placeStopIndex, ATM_TOLERANCE);
    var timeToEnergySave = performanceData && performanceData.systemTimeToEnergySave > 0
        ? performanceData.systemTimeToEnergySave
        : undefined;
    return USE_LOCAL_GRAPH_DATA
        ? mockPerformanceGraphData
        : {
            systemProperty: {
                timeData: (_f = performanceData === null || performanceData === void 0 ? void 0 : performanceData.vacuumLevelTimeData) !== null && _f !== void 0 ? _f : [],
                airConsumptionVsTime: (_g = performanceData === null || performanceData === void 0 ? void 0 : performanceData.airConsumptionVsTime) !== null && _g !== void 0 ? _g : [],
                robotMotionVsTime: (_h = performanceData === null || performanceData === void 0 ? void 0 : performanceData.robotMotionVsTime) !== null && _h !== void 0 ? _h : [],
                psIndex: psIndex,
                esIndex: esIndex,
                cupReleaseIndex: cupReleaseIndex,
                showEs: showEnergySave && timeToEnergySave,
                placeStartIndex: placeStartIndex,
                airConsumptionTotal: (_j = performanceData === null || performanceData === void 0 ? void 0 : performanceData.airConsumptionTotal) !== null && _j !== void 0 ? _j : 0,
                liftingForceHorizontalVsTime: (_k = performanceData === null || performanceData === void 0 ? void 0 : performanceData.liftingForceHorizontalVsTime) !== null && _k !== void 0 ? _k : [],
                liftingForceVerticalVsTime: (_l = performanceData === null || performanceData === void 0 ? void 0 : performanceData.liftingForceVerticalVsTime) !== null && _l !== void 0 ? _l : [],
            },
            nodeData: nodeData,
            governingPump: getGoverningPump(nodeData, (_m = sanityCheckData === null || sanityCheckData === void 0 ? void 0 : sanityCheckData.governingPumpId) !== null && _m !== void 0 ? _m : 0),
            vacuumPumps: getVacuumPumps(nodeData),
            suctionCups: getSuctionCups(nodeData),
        };
});
/**
 * The Recharts x-axis time (seconds) at which the cups reach atmospheric — the
 * physically-accurate "ATM" moment, derived from the cup vacuum curves
 * (`cupReleaseIndex`) and the same point the graph's vertical ATM reference
 * line is drawn at. Undefined until graph data with a resolved release index is
 * available.
 *
 * This is the single source of truth for ATM: the timeline/SVG ATM marker and
 * the play-head ATM snap target both derive from it (via `displayTimeToPercent`)
 * so the SVG ATM line, the graph ATM reference line and the PlayLine all
 * coincide — including when zoomed in, where any drift between this and the
 * timeline's release-end boundary would otherwise be magnified.
 */
export var selectAtmReleaseTime = createSelector(selectGraphData, function (graphData) {
    var _a = graphData.systemProperty, timeData = _a.timeData, cupReleaseIndex = _a.cupReleaseIndex;
    if (cupReleaseIndex === undefined || cupReleaseIndex <= 0)
        return undefined;
    var time = timeData[cupReleaseIndex];
    return typeof time === "number" ? time : undefined;
});
/**
 * The Recharts x-axis times (seconds) by which every cup has reached part
 * secured and levelled out (see `findCupsSettledTimes`). Drive the default
 * zoom of the application speed box so it shows every cup's way up to PS.
 */
export var selectCupsSettledTimes = createSelector(selectGraphData, function (graphData) {
    return findCupsSettledTimes(graphData.suctionCups, graphData.systemProperty.timeData, graphData.systemProperty.placeStartIndex);
});
// We need to parse the pure graph data from the calculation API to fit the expected data structure used by Recharts.
export var selectParsedGraphData = createSelector(selectGraphData, parseGraphDataForRecharts);
/**
 * Returns the vacuum level domain derived from the non-energy-saving data,
 * regardless of which dataset is currently displayed. Used so the cup
 * animation colour always scales against the full (non-ES) vacuum range.
 */
export var selectNonEsVacuumLevelDomain = createSelector(selectPerformanceData, function (performanceData) {
    if (!performanceData)
        return undefined;
    var nonEsData = performanceData.energySavingOff;
    var nodeData = nonEsData.nodeData;
    if (!nodeData)
        return undefined;
    return computeNonEsVacuumLevelDomain(nodeData);
});
